/**
 * Core Layer - Routes
 * Route configuration and utilities
 */

import { siteConfig } from "@/config/site";
import { ROUTES } from "@/constants";

/**
 * Route configuration including metadata
 */
export interface RouteConfig {
  path: string;
  requiresAuth: boolean;
  title?: string;
  description?: string;
  permissions?: string[];
}

/**
 * Main application routes with configuration
 */
export const appRoutes: Record<keyof typeof ROUTES, RouteConfig> = {
  HOME: {
    path: ROUTES.HOME,
    requiresAuth: false,
    title: "Home",
    description: `Welcome to ${siteConfig.name}`,
  },
  DASHBOARD: {
    path: ROUTES.DASHBOARD,
    requiresAuth: true,
    title: "Dashboard",
    description: "Manage your farm operations",
  },
  FARMER_SETUP: {
    path: ROUTES.FARMER_SETUP,
    requiresAuth: true,
    title: "Farmer Setup",
    description: "Set up your farm organization",
  },
  LOGIN: {
    path: ROUTES.LOGIN,
    requiresAuth: false,
    title: "Login",
    description: `Sign in to ${siteConfig.name}`,
  },
  SIGNUP: {
    path: ROUTES.SIGNUP,
    requiresAuth: false,
    title: "Sign Up",
    description: `Create a ${siteConfig.name} account`,
  },
  FORGOT_PASSWORD: {
    path: ROUTES.FORGOT_PASSWORD,
    requiresAuth: false,
    title: "Forgot Password",
    description: "Reset your password",
  },
};

/**
 * Helper functions to work with routes
 */
export const routeUtils = {
  /**
   * Check if a route requires authentication
   */
  requiresAuth: (path: string): boolean => {
    return Object.values(appRoutes).some(
      (route) => path.startsWith(route.path) && route.requiresAuth
    );
  },

  /**
   * Generate the login URL with callback
   */
  getLoginUrl: (callbackUrl?: string): string => {
    if (!callbackUrl) return appRoutes.LOGIN.path;
    return `${appRoutes.LOGIN.path}?callbackUrl=${encodeURIComponent(
      callbackUrl
    )}`;
  },

  /**
   * Generate the home URL
   */
  getHomeUrl: (): string => {
    return appRoutes.HOME.path;
  },

  /**
   * Generate the dashboard URL
   */
  getDashboardUrl: (): string => {
    return appRoutes.DASHBOARD.path;
  },

  /**
   * Check if a path is the home path
   */
  isHomePath: (path: string): boolean => {
    return path === appRoutes.HOME.path;
  },

  /**
   * Check if a path is a public path (doesn't require auth)
   */
  isPublicPath: (path: string): boolean => {
    return !routeUtils.requiresAuth(path);
  },

  /**
   * Check if a path is a private path (requires auth)
   */
  isPrivatePath: (path: string): boolean => {
    return routeUtils.requiresAuth(path);
  },

  /**
   * Check if a path is an auth path (login, signup, forgot-password)
   * These are pages that authenticated users should NOT access
   */
  isAuthPath: (path: string): boolean => {
    const authOnlyPaths = [
      appRoutes.LOGIN.path,
      appRoutes.SIGNUP.path,
      appRoutes.FORGOT_PASSWORD.path,
      "/sign_up", // OAuth failure redirect (legacy backend sends users here)
    ];
    return authOnlyPaths.some((authPath) => path.startsWith(authPath));
  },
};
